import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/programmes", label: "Programmes" },
  { to: "/safe-place", label: "Safe Place" },
  { to: "/donate", label: "Donate" },
  { to: "/contact", label: "Contact" },
];

const NavItem = ({ to, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase()}-link`}
    className={({ isActive }) => `group relative py-1 text-sm font-medium ${isActive ? "text-ochre" : "text-sand"}`}
  >
    {label}
    <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-current transition-transform duration-500 group-hover:scale-x-100" />
  </NavLink>
);

const AuthLinks = ({ onClick }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!user) {
    return (
      <>
        <NavItem to="/login" label="Login" onClick={onClick} />
        <Link to="/register" onClick={onClick} data-testid="nav-join-button" className="rounded-full bg-sand px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-ochre hover:text-sand">Join a course</Link>
      </>
    );
  }
  return (
    <>
      <NavItem to={user.role === "admin" ? "/admin" : user.role === "driver" ? "/driver" : "/dashboard"} label={user.role === "admin" ? "Admin" : user.role === "driver" ? "Driver" : "Dashboard"} onClick={onClick} />
      {user.role === "admin" && <NavItem to="/driver" label="Driver" onClick={onClick} />}
      <button
        data-testid="nav-logout-button"
        onClick={async () => { await logout(); onClick?.(); nav("/"); }}
        className="flex items-center gap-2 rounded-full border border-sand/30 px-4 py-2 text-sm text-sand transition-colors hover:border-ochre hover:text-ochre"
      >
        <LogOut className="h-4 w-4" /> Log out
      </button>
    </>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-sand/10 bg-ink/75 text-sand backdrop-blur-xl print:hidden">
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 sm:px-10">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => <NavItem key={l.to} {...l} />)}
          <span className="h-5 w-px bg-sand/20" />
          <AuthLinks />
        </nav>
        <button data-testid="mobile-menu-toggle" className="md:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-sand/10 md:hidden"
          >
            <div className="flex flex-col items-start gap-5 px-5 py-8">
              {links.map((l) => <NavItem key={l.to} {...l} onClick={() => setOpen(false)} />)}
              <AuthLinks onClick={() => setOpen(false)} />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
